import { DropdownMenu as M, Popover as P, Tooltip as T } from 'radix-ui';
import type { ReactNode } from 'react';

import { cx } from './cx';

export const MenuRoot = M.Root;
export const MenuTrigger = M.Trigger;

export function MenuContent({
  children,
  align = 'end',
  className,
}: {
  children: ReactNode;
  align?: 'start' | 'end' | 'center';
  className?: string;
}) {
  return (
    <M.Portal>
      <M.Content
        align={align}
        sideOffset={6}
        className={cx(
          'z-[150] min-w-[200px] rounded-xl border border-line bg-surface p-1.5 shadow-card',
          className,
        )}
      >
        {children}
      </M.Content>
    </M.Portal>
  );
}

export function MenuItem({
  children,
  onSelect,
  danger,
  disabled,
  icon,
}: {
  children: ReactNode;
  onSelect: () => void;
  danger?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
}) {
  return (
    <M.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cx(
        'flex min-h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-bg2',
        danger ? 'text-danger' : 'text-text',
      )}
    >
      {icon}
      {children}
    </M.Item>
  );
}

export function MenuLabel({ children }: { children: ReactNode }) {
  return (
    <M.Label className="px-2.5 pt-2 pb-1.5 text-xs font-semibold tracking-[.08em] text-muted uppercase">
      {children}
    </M.Label>
  );
}

export const MenuSeparator = () => <M.Separator className="my-1 h-px bg-line" />;
export const MenuRadioGroup = M.RadioGroup;
export const MenuRadioItem = M.RadioItem;

export const PopoverRoot = P.Root;
export const PopoverTrigger = P.Trigger;
export const PopoverAnchor = P.Anchor;

export function PopoverContent({
  children,
  className,
  label,
  align = 'end',
}: {
  children: ReactNode;
  className?: string;
  label?: string;
  align?: 'start' | 'end' | 'center';
}) {
  return (
    <P.Portal>
      <P.Content
        align={align}
        sideOffset={8}
        aria-label={label}
        className={cx(
          'z-[41] rounded-xl border border-line bg-surface shadow-card outline-none',
          className,
        )}
      >
        {children}
      </P.Content>
    </P.Portal>
  );
}

export function Tip({
  label,
  children,
  side = 'right',
}: {
  label: string;
  children: ReactNode;
  side?: 'right' | 'top' | 'bottom' | 'left';
}) {
  return (
    <T.Root delayDuration={250}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          className="z-[300] rounded-md bg-navy px-2.5 py-1.5 text-xs font-medium text-white shadow-card"
        >
          {label}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}

export const TooltipProvider = T.Provider;
